import React from 'react';

import { Formik, Form as FormikForm } from 'formik';

import { Alert, Modal } from '@shared/components/bootstrap';
import { FormikCheckboxFormGroup } from '@shared/components/fields/formik/formik_checkbox_form_group';
import { FormikInputFormGroup } from '@shared/components/fields/formik/formik_input_form_group';
import { FormikMultiselectFormGroup } from '@shared/components/fields/formik/formik_multiselect_form_group';
import { FormikSelectFormGroup } from '@shared/components/fields/formik/formik_select_form_group';

import { client } from '@admin/libraries/apollo';
import { ClutterGeoEnum, Status, TeamInput, useTeamUpsertMutation } from '@admin/schema';
import { TeamManagementSummaryFragment } from '@admin/schema';

import {
  FormFullWidth,
  FormGrid,
  FormSection,
  FormSectionHint,
  FormSectionTitle,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalShell,
  ModalSubtitle,
  ModalTitle,
  PrimaryButton,
  SecondaryButton,
} from './teams_styles';

type Option = {
  id: string;
  name: string;
};

const displayableGeo = (geo: string) => geo.replace(/_/g, ' ');

const GEO_OPTIONS = Object.values(ClutterGeoEnum)
  .sort()
  .map((value) => ({
    label: displayableGeo(value),
    value,
  }));

export type TeamFormValues = TeamInput & {
  parentID?: string | null;
  regionID?: string | null;
  teamTypeID?: string | null;
};

export const UpsertModal: React.FC<{
  team?: TeamManagementSummaryFragment;
  teamOptions: TeamManagementSummaryFragment[];
  regionOptions: Option[];
  teamTypeOptions: Option[];
  userOptions: Option[];
  closeModal(): void;
}> = ({ team, teamOptions, regionOptions, teamTypeOptions, userOptions, closeModal }) => {
  const [save, { loading, data }] = useTeamUpsertMutation({
    client,
    onCompleted: ({ result }) => {
      if (result?.status === Status.Ok) {
        closeModal();
      }
    },
  });

  const parentOptions = teamOptions
    .filter((option) => option.id !== team?.id)
    .map((option) => ({ id: option.id, name: option.name }));

  const onSubmit = (values: TeamFormValues) => {
    save({
      variables: {
        id: team?.id,
        input: {
          name: values.name,
          assignable: values.assignable,
          geo: values.geo || null,
          parentID: values.parentID || null,
          regionID: values.regionID || null,
          teamTypeID: values.teamTypeID || null,
          ownerIDs: values.ownerIDs ?? [],
        },
      },
    });
  };

  return (
    <Modal onClose={closeModal}>
      <Formik<TeamFormValues>
        initialValues={{
          name: team?.name ?? '',
          assignable: team?.assignable ?? true,
          geo: team?.geo ?? undefined,
          parentID: team?.parent?.id ?? null,
          regionID: team?.region?.id ?? null,
          teamTypeID: team?.teamType?.id ?? null,
          ownerIDs: team?.owners.map((owner) => owner.id) ?? [],
        }}
        onSubmit={onSubmit}
      >
        <FormikForm>
          <ModalShell className="modal-content">
            <ModalHeader>
              <ModalTitle>{team ? 'Edit team' : 'Create team'}</ModalTitle>
              <ModalSubtitle>
                {team ? 'Update team details and owners.' : 'Set up a new team for scheduling and assignment.'}
              </ModalSubtitle>
            </ModalHeader>
            <ModalBody>
              {data?.result?.error && <Alert style="danger">{data.result.error}</Alert>}

              <FormSection>
                <FormSectionTitle>Basics</FormSectionTitle>
                <FormSectionHint>Name and whether this team can be assigned to users.</FormSectionHint>
                <FormGrid>
                  <FormFullWidth>
                    <FormikInputFormGroup label="Name" name="name" required />
                  </FormFullWidth>
                  <FormFullWidth>
                    <FormikCheckboxFormGroup label="Assignable" name="assignable" id="team_assignable" />
                  </FormFullWidth>
                </FormGrid>
              </FormSection>

              <FormSection>
                <FormSectionTitle>Organization</FormSectionTitle>
                <FormSectionHint>Geo, region, type, and parent hierarchy.</FormSectionHint>
                <FormGrid>
                  <FormikSelectFormGroup label="Geo" name="geo">
                    <option value="">None</option>
                    {GEO_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </FormikSelectFormGroup>
                  <FormikSelectFormGroup label="Region" name="regionID">
                    <option value="">None</option>
                    {regionOptions.map((region) => (
                      <option key={region.id} value={region.id}>
                        {region.name}
                      </option>
                    ))}
                  </FormikSelectFormGroup>
                  <FormikSelectFormGroup label="Team type" name="teamTypeID">
                    <option value="">None</option>
                    {teamTypeOptions.map((teamType) => (
                      <option key={teamType.id} value={teamType.id}>
                        {teamType.name}
                      </option>
                    ))}
                  </FormikSelectFormGroup>
                  <FormikSelectFormGroup label="Parent team" name="parentID">
                    <option value="">None</option>
                    {parentOptions.map((parentTeam) => (
                      <option key={parentTeam.id} value={parentTeam.id}>
                        {parentTeam.name}
                      </option>
                    ))}
                  </FormikSelectFormGroup>
                </FormGrid>
              </FormSection>

              <FormSection>
                <FormSectionTitle>Team owners</FormSectionTitle>
                <FormSectionHint>Users who own and supervise this team.</FormSectionHint>
                <FormikMultiselectFormGroup label="Team owners" name="ownerIDs" entries={userOptions} />
              </FormSection>
            </ModalBody>
            <ModalFooter>
              <SecondaryButton type="button" onClick={closeModal}>
                Cancel
              </SecondaryButton>
              <PrimaryButton type="submit" disabled={loading}>
                {loading ? 'Saving…' : 'Save team'}
              </PrimaryButton>
            </ModalFooter>
          </ModalShell>
        </FormikForm>
      </Formik>
    </Modal>
  );
};
